// src/components/Reviews/ReviewSection.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { reviewService } from '../../services/reviewService';
import { authService } from '../../services/authService';

const ReviewSection = ({ outletVariantId }) => {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useUser();
  const [reviewsData, setReviewsData] = useState({ totalCount: 0, reviews: [] });
  const [loading, setLoading] = useState(true);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  const [editReviewId, setEditReviewId] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  // Image Fullscreen View State
  const [fullScreenImg, setFullScreenImg] = useState(null);
  
  // Real-time user profile state
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    fetchReviews();
  }, [outletVariantId]);

  useEffect(() => {
    if (isLoggedIn()) {
      authService.getProfile()
        .then(data => {
          setCurrentUser(data);
        })
        .catch(err => console.log("Failed to fetch profile for reviews:", err));
    }
  }, [isLoggedIn]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const data = await reviewService.getSummary(outletVariantId);
      setReviewsData(data || { totalCount: 0, reviews: [] });
    } catch (err) {
      console.error("Failed to load reviews", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (existingReview = null) => {
    if (!isLoggedIn()) {
      navigate('/login');
      return;
    }

    if (existingReview) {
      setEditReviewId(existingReview.reviewId);
      setRating(existingReview.rating || 0);
      setComment(existingReview.comment || '');
      setSelectedFiles([]);
      setPreviewUrls(existingReview.mediaUrls || []);
    } else {
      setEditReviewId(null);
      setRating(0);
      setComment('');
      setSelectedFiles([]);
      setPreviewUrls([]);
    }
    setErrorMsg('');
    setIsModalOpen(true);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    
    const filePreviews = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(filePreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setErrorMsg("Please provide a star rating.");
      return;
    }
    if (!comment.trim()) {
      setErrorMsg("Please write a review comment.");
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      const reviewPayload = {
        outletVariantId: Number(outletVariantId),
        rating,
        comment
      };

      if (editReviewId) {
        await reviewService.editReview(editReviewId, reviewPayload, selectedFiles);
      } else {
        await reviewService.addReview(reviewPayload, selectedFiles);
      }

      setIsModalOpen(false);
      fetchReviews(); 
    } catch (err) {
      console.error("Review submit error:", err);
      setErrorMsg("Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete your review?")) return;
    try {
      await reviewService.deleteReview(reviewId);
      alert("Review deleted successfully!");
      fetchReviews();
    } catch (err) {
      console.error("Error deleting review:", err);
      alert("Failed to delete review. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const avgRating = reviewsData.reviews.length > 0 
    ? (reviewsData.reviews.reduce((acc, r) => acc + r.rating, 0) / reviewsData.reviews.length).toFixed(1)
    : 0;

  const checkIsMyReview = (reviewUserName) => {
    if (!isLoggedIn() || !reviewUserName) return false;
    const checkUser = currentUser || user;
    if (!checkUser) return false;

    const rName = String(reviewUserName).toLowerCase().replace(/\s+/g, '');
    const uName = String(checkUser.name || '').toLowerCase().replace(/\s+/g, '');
    const uEmailPrefix = String(checkUser.email || '').split('@')[0].toLowerCase().replace(/\s+/g, '');
    
    return (uName && rName === uName) || (uEmailPrefix && rName === uEmailPrefix);
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight">Customer Reviews</h2>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex text-[#FF7A59] text-sm">
              {[1, 2, 3, 4, 5].map(star => (
                <i key={star} className={`fas fa-star ${star <= avgRating ? '' : 'text-gray-200'}`}></i>
              ))}
            </div>
            <span className="font-bold text-gray-700">{avgRating} out of 5</span>
            <span className="text-gray-400 text-sm font-medium">({reviewsData.totalCount} reviews)</span>
          </div>
        </div>
        
        <button 
          onClick={() => handleOpenModal()} 
          className="bg-[#0F2F63] hover:bg-[#1a4a8a] text-white px-6 py-2.5 rounded-xl font-bold shadow-md transition-colors whitespace-nowrap"
        >
          {isLoggedIn() ? 'Write a Review' : 'Login to Review'}
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-10">
          <i className="fas fa-spinner fa-spin text-3xl text-[#0F2F63] mb-3"></i>
          <p className="text-gray-500 font-medium text-sm">Loading reviews...</p>
        </div>
      ) : reviewsData.reviews.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 border border-dashed border-gray-200 rounded-2xl">
          <i className="fas fa-comment-slash text-4xl text-gray-300 mb-3"></i>
          <p className="text-gray-600 font-bold mb-1">No reviews yet</p>
          <p className="text-gray-400 text-sm">Be the first to share your thoughts on this product!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviewsData.reviews.map(review => (
            <div key={review.reviewId} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-100 text-[#0F2F63] flex items-center justify-center font-black text-lg shadow-sm">
                    {review.userName ? review.userName.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 leading-tight">{review.userName}</p>
                    <p className="text-xs text-gray-400 font-medium">{formatDate(review.createdAt)}</p>
                  </div>
                </div>
                
                {/* Changed: Edit and Delete buttons moved to Top Right corner */}
                {checkIsMyReview(review.userName) && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleOpenModal(review)} className="text-[11px] font-bold text-sky-600 hover:text-sky-800 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5">
                      <i className="fas fa-pen"></i> <span className="hidden sm:inline">Edit</span>
                    </button>
                    <button onClick={() => handleDeleteReview(review.reviewId)} className="text-[11px] font-bold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5">
                      <i className="fas fa-trash"></i> <span className="hidden sm:inline">Delete</span>
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex text-[#FF7A59] text-xs mb-3">
                {[1, 2, 3, 4, 5].map(star => (
                  <i key={star} className={`fas fa-star ${star <= review.rating ? '' : 'text-gray-200'}`}></i>
                ))}
              </div>
              
              <p className="text-gray-700 text-sm leading-relaxed mb-4">{review.comment}</p>
              
              {review.mediaUrls && review.mediaUrls.length > 0 && (
                <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-2">
                  <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
                  {review.mediaUrls.map((img, idx) => (
                    <img 
                      key={idx} 
                      src={img} 
                      alt="Review" 
                      onClick={() => setFullScreenImg(img)}
                      className="w-20 h-20 object-cover rounded-xl border border-gray-200 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity" 
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Write/Edit Review Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col animate-fade-in">
            <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-gray-50">
              <h2 className="text-lg font-black text-[#0F2F63]">{editReviewId ? 'Edit Review' : 'Write a Review'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-gray-500 hover:text-red-500 shadow-sm transition-colors">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="p-5">
              {errorMsg && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-bold rounded-xl border border-red-100"><i className="fas fa-exclamation-circle mr-2"></i>{errorMsg}</div>}
              
              <form onSubmit={handleSubmit} className="space-y-5">
                
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Overall Rating *</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button 
                        key={star} 
                        type="button"
                        onClick={() => setRating(star)}
                        className={`text-2xl transition-transform hover:scale-110 ${star <= rating ? 'text-[#FF7A59]' : 'text-gray-200'}`}
                      >
                        <i className="fas fa-star"></i>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Your Review *</label>
                  <textarea 
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows="4"
                    className="w-full px-4 py-3 rounded-xl border bg-gray-50 border-gray-200 text-gray-900 focus:border-[#FF7A59] focus:ring-2 focus:ring-[#FF7A59]/20 outline-none transition-all font-semibold resize-none"
                    placeholder="What did you like or dislike? What did you use this product for?"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Add Photos (Optional)</label>
                  
                  <div className="flex flex-wrap gap-3 mb-2">
                    {previewUrls.map((url, idx) => (
                      <img key={idx} src={url} alt="Preview" className="w-16 h-16 object-cover rounded-lg border border-gray-200 shadow-sm" />
                    ))}
                    
                    <label className="w-16 h-16 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors text-gray-400 hover:text-[#0F2F63]">
                      <i className="fas fa-camera text-xl mb-1"></i>
                      <span className="text-[9px] font-bold uppercase tracking-widest">Add</span>
                      <input type="file" multiple accept="image/*" onChange={handleFileChange} className="hidden" />
                    </label>
                  </div>
                </div>

                <div className="pt-2">
                  <button type="submit" disabled={isSubmitting} className="w-full py-3.5 bg-[#0F2F63] hover:bg-[#1a4a8a] text-white rounded-xl font-black transition-colors shadow-lg disabled:opacity-70 flex items-center justify-center gap-2">
                    {isSubmitting ? <i className="fas fa-spinner fa-spin"></i> : 'Submit Review'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Image Viewer Modal */}
      {fullScreenImg && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" 
          onClick={() => setFullScreenImg(null)}
        >
          <button 
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition-colors"
            onClick={() => setFullScreenImg(null)}
          >
            <i className="fas fa-times text-xl"></i>
          </button>
          <img 
            src={fullScreenImg} 
            alt="Review Fullscreen" 
            className="max-w-full max-h-full object-contain rounded-lg" 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}
    </div>
  );
};

export default ReviewSection;